import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AiSolutionUsageDetail, AiSolutionUsageStateChange } from '@/app/interfaces/ai-solution-usage';
import { AiSolutionUsageService } from '@/app/services/ia-services/ai-solution-usage.service';
import { BackButtonComponent } from '@/app/shared/back-button/back-button.component';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { getProjectStateInfo } from '@/app/shared/mappers/project-state.mapper';
import { DesyAngularModule } from 'desy-angular';

@Component({
  selector: 'app-ai-solution-usage-detail',
  standalone: true,
  imports: [CommonModule, BackButtonComponent, ButtonsComponent, DesyAngularModule],
  templateUrl: './ai-solution-usage-detail.component.html',
  styleUrl: './ai-solution-usage-detail.component.scss',
})
export class AiSolutionUsageDetailComponent implements OnInit {
  readonly detail = signal<AiSolutionUsageDetail | null>(null);
  readonly loading = signal(true);
  readonly notFound = signal(false);
  readonly errorMessage = signal('');
  readonly stateTextFormat = signal('');
  readonly colorBox = signal('');
  private usageId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private aiSolutionUsageService: AiSolutionUsageService
  ) {}

  async ngOnInit(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('id');

    if (!id) {
      this.loading.set(false);
      this.errorMessage.set('No se ha indicado el identificador del uso de solución IA.');
      return;
    }

    this.usageId = Number(id);

    try {
      const detail = await this.aiSolutionUsageService.getAiSolutionUsageDetail(id);
      this.detail.set(detail);
      this.setStateInfo(detail.state);
    } catch (error) {
      if (error?.response?.status === 404) {
        this.notFound.set(true);
      } else {
        this.errorMessage.set('No se ha podido cargar el detalle del uso de solución IA.');
      }
    } finally {
      this.loading.set(false);
    }
  }

  async accept(): Promise<void> {
    await this.changeState('APPROVED');
  }

  async reject(): Promise<void> {
    await this.changeState('REJECTED');
  }

  private setStateInfo(state?: string | null): void {
    const stateInfo = getProjectStateInfo(state);

    this.stateTextFormat.set(state?.toUpperCase() === 'PENDING' ? 'Pendiente de evaluación' : stateInfo.label);
    this.colorBox.set(stateInfo.className);
  }

  private async changeState(state: AiSolutionUsageStateChange): Promise<void> {
    if (this.usageId === null) {
      return;
    }

    await this.aiSolutionUsageService.changeAiSolutionUsageState({
      codigoUsoSolucion: this.usageId,
      state,
      comentario: '',
    });
    await this.router.navigate(['/catalogo-ia/uso-de-solucion-ia/listado-uso-de-solucion-ia']);
  }
}
